import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';

function Feedback() {
  const [searchParams] = useSearchParams();
  const [leadId, setLeadId] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 0,
    comment: '',
    sendUpdates: false
  });
  const [hoveredStar, setHoveredStar] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isLoadingLead, setIsLoadingLead] = useState(false);

  // Fetch lead details when leadid is present
  useEffect(() => {
    const leadIdParam = searchParams.get('leadid');
    if (leadIdParam) {
      setLeadId(leadIdParam);
      fetchLeadDetails(leadIdParam);
    }
  }, [searchParams]);

  const fetchLeadDetails = async (hashedLeadId) => {
    setIsLoadingLead(true);
    try {
      const token = generateToken();
      const response = await fetch(`/api/admin/leaddetails?leadid=${hashedLeadId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: token
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setFormData(prev => ({
            ...prev,
            name: result.data.name || '',
            email: result.data.email || ''
          }));
        }
      } else {
        console.error('Failed to fetch lead details:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching lead details:', error);
    } finally {
      setIsLoadingLead(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || formData.rating === 0 || !formData.comment) {
      setSubmitMessage('Please fill in all required fields');
      setTimeout(() => setSubmitMessage(''), 3000);
      return;
    }

    setIsSubmitting(true);
    
    try {
      const token = generateToken();
      const response = await fetch('/api/admin/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: token
        },
        body: JSON.stringify({
          leadId: leadId,
          rating: formData.rating,
          comment: formData.comment,
          futureUpdates: formData.sendUpdates
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitMessage('Thank you for your feedback!');
        setFormData({
          name: formData.name,
          email: formData.email,
          rating: 0,
          comment: '',
          sendUpdates: false
        });
      } else {
        setSubmitMessage(result.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitMessage('Error submitting feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  return (
    <div className="feedback-page" style={{ 
      maxWidth: '100%', 
      margin: '0 auto', 
      padding: '2rem 1rem',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)'
    }}>
      <div style={{
        maxWidth: '700px',
        margin: '0 auto',
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(44,62,80,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #DA291C 0%, #b91f14 100%)',
          padding: '2rem',
          textAlign: 'center',
          color: '#fff'
        }}>
          <h1 style={{ 
            margin: '0 0 0.5rem 0',
            fontSize: '2.5rem',
            fontWeight: '700'
          }}>
            Share Your Feedback
          </h1>
          <p style={{
            margin: 0,
            fontSize: '1.1rem',
            opacity: 0.95
          }}>
            Your thoughts help us improve and serve you better
          </p>
        </div>
        
        <form onSubmit={handleSubmit} style={{ padding: '2rem' }}>
          {/* Name Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem',
              color: '#2c3e50',
              fontWeight: '600'
            }}>
              Name <span style={{ color: '#DA291C' }}>*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isLoadingLead}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e8eef5',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.3s',
                boxSizing: 'border-box',
                backgroundColor: isLoadingLead ? '#f5f7fa' : '#fff',
                cursor: isLoadingLead ? 'not-allowed' : 'text'
              }}
              onFocus={(e) => !isLoadingLead && (e.target.style.borderColor = '#DA291C')}
              onBlur={(e) => e.target.style.borderColor = '#e8eef5'}
            />
          </div>

          {/* Email Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem',
              color: '#2c3e50',
              fontWeight: '600'
            }}>
              Email <span style={{ color: '#DA291C' }}>*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isLoadingLead}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e8eef5',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.3s',
                boxSizing: 'border-box',
                backgroundColor: isLoadingLead ? '#f5f7fa' : '#fff',
                cursor: isLoadingLead ? 'not-allowed' : 'text'
              }}
              onFocus={(e) => !isLoadingLead && (e.target.style.borderColor = '#DA291C')}
              onBlur={(e) => e.target.style.borderColor = '#e8eef5'}
            />
          </div>

          {/* Rating Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem',
              color: '#2c3e50',
              fontWeight: '600'
            }}>
              Rating <span style={{ color: '#DA291C' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleRatingClick(star)}
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(0)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '2rem',
                    cursor: 'pointer',
                    color: (hoveredStar >= star || formData.rating >= star) ? '#FFD700' : '#e8eef5',
                    transition: 'color 0.2s',
                    padding: '0.25rem'
                  }}
                  aria-label={`Rate ${star} stars`}
                >
                  ★
                </button>
              ))}
            </div>
            {formData.rating > 0 && (
              <span style={{ 
                fontSize: '0.875rem', 
                color: '#7f8c8d',
                marginTop: '0.25rem',
                display: 'block'
              }}>
                You rated: {formData.rating} star{formData.rating !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Comment Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem',
              color: '#2c3e50',
              fontWeight: '600'
            }}>
              Comment <span style={{ color: '#DA291C' }}>*</span>
            </label>
            <textarea
              name="comment"
              value={formData.comment}
              onChange={handleChange}
              required
              rows="5"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e8eef5',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.3s',
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#DA291C'}
              onBlur={(e) => e.target.style.borderColor = '#e8eef5'}
            />
          </div>

          {/* Send Updates Checkbox */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ 
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: '#2c3e50'
            }}>
              <input
                type="checkbox"
                name="sendUpdates"
                checked={formData.sendUpdates}
                onChange={handleChange}
                style={{
                  marginRight: '0.5rem',
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer'
                }}
              />
              <span>Send me future updates and news</span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '1rem',
              background: isSubmitting ? '#95a5a6' : 'linear-gradient(135deg, #DA291C 0%, #b91f14 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'transform 0.2s',
              boxShadow: '0 4px 12px rgba(218, 41, 28, 0.3)'
            }}
            onMouseEnter={(e) => !isSubmitting && (e.target.style.transform = 'translateY(-2px)')}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>

          {/* Submit Message */}
          {submitMessage && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              background: submitMessage.includes('Thank you') ? '#d4edda' : '#f8d7da',
              color: submitMessage.includes('Thank you') ? '#155724' : '#721c24',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              {submitMessage}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default Feedback;
